import { CommonModule } from '@angular/common';
import { Component, OnInit, SecurityContext, inject, ChangeDetectionStrategy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { IonContent, IonFooter, IonHeader, IonText, IonToolbar } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { createSelector, Store } from '@ngrx/store';
import { catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';

import { AttachmentParentType } from '@ninety/mobile/platform/api';
import {
  AttachmentsComponent,
  DescriptionComponent,
  LoadingComponent,
  NtyBackButtonComponent,
  NtyDatePickerComponent,
  TitleComponent,
} from '@ninety/mobile/platform/components';
import {
  CurrentUserSelectors,
  LanguageSelectors,
  NavigationService,
  UniversalCreateActions,
} from '@ninety/mobile/platform/state';
import { TerraStatusBadgeModule } from '@ninety/terra';

import { DiscussionModel } from '../../../models/discussion.models';
import { PatchDiscussionDto, PatchDiscussionDtoImpl } from '../../../models/dto/patch-discussion.dto';
import { DiscussionDetailForm } from '../../../models/form/discussion-detail-form';
import { OverduePipe } from '../../../pipes/overdue.pipe';
import { DiscussionApiService } from '../../../services/discussion-api.service';
import { DiscussionDetailActions as actions } from '../../../state/actions';
import { selectActiveDiscussionId } from '../../../state/selectors';
import { DiscussionStateModule } from '../../../state/state';
import { DiscussionFormParticipantsComponent } from '../../discussion-participants/discussion-form-participants.component';
import { DiscussionParticipantsStatusComponent } from '../../discussion-participants/discussion-participants-status.component';
import { DiscussionStatusBadgeComponent } from '../../discussion-status-badge/discussion-status-badge.component';
import { DiscussionDetailFooterComponent } from '../discussion-detail-footer/discussion-detail-footer.component';

interface State {
  isEditing: boolean;
  discussion: DiscussionModel | null;
  attachmentParentType: AttachmentParentType.Conversation;
}

@Component({
  selector: 'nty-discussion-detail-container',
  imports: [
    IonContent,
    IonFooter,
    IonHeader,
    IonText,
    IonToolbar,
    CommonModule,
    ReactiveFormsModule,
    LetDirective,
    DiscussionStateModule,
    LoadingComponent,
    NtyBackButtonComponent,
    DiscussionStatusBadgeComponent,
    DiscussionFormParticipantsComponent,
    DiscussionParticipantsStatusComponent,
    AttachmentsComponent,
    DiscussionDetailFooterComponent,
    OverduePipe,
    TitleComponent,
    DescriptionComponent,
    NtyDatePickerComponent,
    TerraStatusBadgeModule,
  ],
  templateUrl: './discussion-detail-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./discussion-detail-container.component.scss'],
})
export class DiscussionDetailContainerComponent extends ComponentStore<State> implements OnInit {
  private readonly store = inject(Store);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly api = inject(DiscussionApiService);
  private readonly route = inject(ActivatedRoute);
  readonly nav = inject(NavigationService);

  constructor() {
    super({ isEditing: false, discussion: null, attachmentParentType: AttachmentParentType.Conversation });
  }

  ngOnInit(): void {
    this.loadDiscussion();
  }

  vm$ = {
    language: this.store.select(LanguageSelectors.discussionLanguage),
    backButtonText: this.store.select(
      createSelector(LanguageSelectors.discussionLanguage, lang => `Back to ${lang?.items ?? ''}`)
    ),
    discussion: this.select(state => state.discussion),
    isEditing: this.select(state => state.isEditing),
    isDiscussionDone: this.select(state => this.isDiscussionDone(state.discussion)),
    descPlaceholder: this.select(state => `${state.isEditing ? 'Add' : 'Tap edit to add'} a description...`),
    attachmentParentType: this.select(state => state.attachmentParentType),
    isManager: this.select(state => state.discussion?.managerUserId).pipe(
      concatLatestFrom(() => this.store.select(CurrentUserSelectors.selectUserId)),
      switchMap(([managerUserId, currentUserId]) => of(managerUserId === currentUserId))
    ),
  };

  form: DiscussionDetailForm = this.fb.group({
    title: this.fb.control('', [Validators.required, Validators.maxLength(80)]),
    description: this.fb.control(''),
    periodStartDate: this.fb.control(this.getISODate(), [Validators.required]),
    periodEndDate: this.fb.control(this.getISODate(), [Validators.required]),
    dueDate: this.fb.control(this.getISODate(), [Validators.required]),
  });

  onDiscussionLoaded = this.updater((state, discussion: DiscussionModel): State => ({
    ...state,
    discussion,
    isEditing: !!this.route.snapshot.data?.['edit'],
  }));
  onEdit = this.updater((state): State => ({ ...state, isEditing: true }));
  onRevert = this.updater((state): State => ({ ...state, isEditing: false }));
  onSave = this.updater((state): State => ({
    ...state,
    isEditing: false,
    discussion: state.discussion
      ? {
          ...state.discussion,
          title: this.form.value.title,
          description: this.form.value.description ?? '',
          periodStartDate: this.form.value.periodStartDate ? new Date(this.form.value.periodStartDate) : new Date(),
          periodEndDate: this.form.value.periodEndDate ? new Date(this.form.value.periodEndDate) : new Date(),
          dueDate: this.form.value.dueDate ? new Date(this.form.value.dueDate) : new Date(),
        }
      : null,
  }));

  revert = this.effect(void$ => void$.pipe(tap(() => this.loadDiscussion())));
  save = this.effect((void$: Observable<void>) =>
    void$.pipe(
      filter(() => !!this.discussionCache),
      tap(() => this.form.disable()),
      map(() => {
        const discussion = this.discussionCache as DiscussionModel;
        const dto = PatchDiscussionDtoImpl.create(discussion, this.form.getRawValue());
        return { discussion, dto, revertFn: this.updateStateOptimistically(discussion, dto) };
      }),
      filter(({ dto }) => Object.keys(dto).length > 0),
      switchMap(({ discussion, dto, revertFn }) =>
        this.api.patchDiscussion(discussion._id, dto).pipe(
          catchError(e => {
            revertFn();
            return of(e);
          })
        )
      )
    )
  );
  delete = this.effect((discussion$: Observable<DiscussionModel>) =>
    discussion$.pipe(
      tap(() => this.nav.goToDiscussions()),
      switchMap(discussion =>
        this.api.patchDiscussion(discussion._id, { isDeleted: true }).pipe(
          tap(() => {
            this.store.dispatch(actions.discussionDetailDeleted({ id: discussion._id }));
          }),
          catchError(e => {
            this.store.dispatch(actions.discussionDetailDeleteFailed({ discussion }));
            console.error(e);
            return of(e);
          })
        )
      )
    )
  );

  openUniversalCreateModal = this.effect((void$: Observable<void>) =>
    void$.pipe(
      tap(() =>
        this.store.dispatch(UniversalCreateActions.open({ dispatchSource: 'DiscussionDetailContainerComponent' }))
      )
    )
  );

  toggleFormCtrls = this.effect((discussion$: Observable<DiscussionModel>) =>
    discussion$.pipe(
      concatLatestFrom(() => this.select(state => state.isEditing)),
      tap(([discussion, isEditing]) => {
        if (!isEditing) return this.form.disable();

        this.form.enable();

        if (discussion.hasManagerStarted || discussion.hasManageeStarted) {
          this.form.controls.periodStartDate.disable();
          this.form.controls.periodEndDate.disable();
        }

        if (this.isDiscussionDone(discussion)) {
          this.form.disable();
        }
      })
    )
  );

  isDiscussionDone(discussion: DiscussionModel | null) {
    if (!discussion) return false;

    const { holdingMeeting, isManagerDone, isManageeDone, isMeetingCompleted, isCompleted } = discussion;
    if (isCompleted) return true;
    if (holdingMeeting && isMeetingCompleted) return true;
    if (!holdingMeeting && isManagerDone && isManageeDone) return true;
    return false;
  }

  private discussionCache: DiscussionModel | null = null;
  private readonly loadDiscussion = this.effect(void$ =>
    void$.pipe(
      concatLatestFrom(() => this.store.select(selectActiveDiscussionId)),
      switchMap(([, discussionId]) =>
        discussionId === this.discussionCache?._id ? of(this.discussionCache) : this.api.getDiscussion(discussionId)
      ),
      tap(discussion => {
        if (discussion) {
          this.onDiscussionLoaded(discussion);
          this.discussionCache = discussion;
          this.initializeForm(discussion);
        }
      })
    )
  );

  private initializeForm(discussion: DiscussionModel): void {
    this.form.patchValue({
      title: discussion.title,
      description: this.getSanitizedTextFromHtml(discussion.description ?? ''),
      periodStartDate: this.getISODate(discussion.periodStartDate),
      periodEndDate: this.getISODate(discussion.periodEndDate),
      dueDate: this.getISODate(discussion.dueDate),
    });
    this.toggleFormCtrls(discussion);
  }

  private getSanitizedTextFromHtml(html: string) {
    if (!html) return '';

    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html);

    if (sanitized) {
      const tempEl = document.createElement('div');
      tempEl.innerHTML = sanitized;
      if (tempEl.textContent) return tempEl.textContent;
      else if (tempEl.innerText) return tempEl.innerText;
      else return '';
    }

    return '';
  }

  private getISODate(date?: Date | string) {
    const val = date ?? new Date();
    return new Date(val).toISOString();
  }

  /**
   * Updates local state and, if applicable, global list state and returns a `revertFn` to reverse the
   * state updates if desired. This is useful for supporting optimistic updates. If an update fails,
   * the revertFn can be called to restore state to its original form.
   */
  private updateStateOptimistically(discussion: DiscussionModel, dto: PatchDiscussionDto) {
    if (this.discussionCache) this.discussionCache = { ...this.discussionCache, ...dto };

    this.store.dispatch(
      actions.discussionDetailSaved({ update: PatchDiscussionDtoImpl.createActionPayload(discussion, dto) })
    );

    return () => {
      this.discussionCache = { ...discussion };
      this.store.dispatch(
        actions.discussionDetailUpdateReverted({
          update: PatchDiscussionDtoImpl.createActionPayload(discussion, discussion),
        })
      );
      this.loadDiscussion();
    };
  }

  dateFormat() {
    const format = { date: { month: 'short', day: '2-digit' } as Intl.DateTimeFormatOptions };
    return format;
  }
}
